import { useCallback, useEffect, useState } from 'react';

import { Button, Input } from 'antd';

import { Icon } from '@eduzz/ui-antd-theme';

import { useImageResizer } from '@/hooks/useImageResizer';
import { cn } from '@/lib/cn';
import { trpc } from '@/lib/trpc';
import useProductStore from '@/stores/productStore';

import { Modrawer } from './Modrawer';

interface IProps {
  open: boolean;
  onCrop: (image: string | null) => void;
}

export const ImageFinder = ({ open, onCrop }: IProps) => {
  const [value, setValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [images, setImages] = useState<string[] | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { resize } = useImageResizer();
  const imageMutation = trpc.imageSearch.searchImages.useMutation({
    onSuccess: images => {
      setImages(images);
    }
  });

  const proxyImageMutation = trpc.imageSearch.proxyImage.useMutation();
  const title = useProductStore(p => p.title);

  useEffect(() => {
    setValue(title || '');
  }, [title]);

  const select = useCallback(async () => {
    if (!selectedImage) {
      return;
    }

    try {
      setIsSaving(true);
      const image = await proxyImageMutation.mutateAsync({ url: selectedImage });

      const result = await resize(image, 800, 800);
      onCrop(result);
      setIsSaving(false);
    } catch (err) {
      console.error(err);
      setIsSaving(false);
    }
  }, [resize, selectedImage, proxyImageMutation, onCrop]);

  return (
    <Modrawer
      open={open}
      width={450}
      height={450}
      onCancel={() => !isSaving && onCrop(null)}
      footer={[
        <Button
          id='crop-ok-button'
          key='ok'
          loading={isSaving}
          className='w-full md:w-auto'
          type='primary'
          disabled={!selectedImage || isSaving}
          onClick={() => select()}
        >
          Concluir
        </Button>
      ]}
      title={'Pesquisar imagem'}
    >
      <form
        className='flex flex-row gap-2'
        method='post'
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
          imageMutation.mutate({ text: value });
        }}
      >
        <Input
          value={value}
          autoFocus
          disabled={imageMutation.isPending || isSaving}
          onChange={e => setValue(e.target.value)}
          id='image-search'
          placeholder='Buscar imagem'
          className='flex-1'
        />
        <Button
          id='search'
          variant='filled'
          color='default'
          icon={<Icon.Search />}
          loading={imageMutation.isPending}
          onClick={() => imageMutation.mutate({ text: value })}
          disabled={imageMutation.isPending || value.trim() === '' || isSaving}
        >
          Pesquisar
        </Button>
      </form>
      <div className='text-content-subtitle pt-1 text-sm'>Disponibilizado por Pexels e Unsplash</div>

      {images && (
        <div className='mt-4 max-h-50 overflow-y-auto'>
          <div className='grid grid-cols-3 gap-1 lg:grid-cols-4'>
            {images.map((image, index) => (
              <div
                key={index}
                className={cn('rounded-default cursor-pointer overflow-hidden border-2 opacity-75 hover:opacity-100', {
                  'dark:border-primary border-blue-500 opacity-100': selectedImage === image,
                  'border-outline-default': selectedImage !== image
                })}
              >
                {image && <img src={image} alt={`Imagem ${index + 1}`} onClick={() => setSelectedImage(image)} />}
              </div>
            ))}
          </div>
        </div>
      )}
    </Modrawer>
  );
};
